import React, { useState, useRef, useEffect } from "react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/20/solid";
import { listboxStyles } from "../../styles/components/common/ListboxStyles";

interface Item {
  id: string | number; // Allow string or number IDs
  [key: string]: any;
}

interface ListboxComponentProps<T> {
  value: T | null;
  onChange: (value: T | null) => void;
  items: T[];
  label: string;
  error?: string;
  displayKey: keyof T;
  required?: boolean;
  className?: string;
  dropdownClassName?: string;
}

const ListboxComponent = <T extends Item>({
  value,
  onChange,
  items,
  label,
  error,
  displayKey,
  required = false,
}: ListboxComponentProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const listboxRef = useRef<HTMLDivElement>(null);

  const displayValue = value ? String(value[displayKey]) : `Select ${label}`;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        listboxRef.current &&
        !listboxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={listboxRef}>
      <label className={listboxStyles.label}>
        {label} {required && <span className={listboxStyles.required}>*</span>}
      </label>
      <div className="relative">
        <button
          type="button"
          className={`${listboxStyles.listboxButton} ${
            error ? listboxStyles.inputError : listboxStyles.inputDefault
          }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={listboxStyles.listboxButtonText}>{displayValue}</span>
          <span className={listboxStyles.listboxButtonIcon}>
            <ChevronDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
          </span>
        </button>
        {isOpen && (
          <ul className={listboxStyles.listboxOptions}>
            <li
              id="0"
              className={listboxStyles.listboxOption}
              onClick={() => {
                onChange(null);
                setIsOpen(false);
              }}
              role="option"
              aria-selected={!value}
            >
              <span
                className={listboxStyles.listboxOptionText({ selected: !value })}
              >
                Select {label}
              </span>
              {!value && (
                <span className={listboxStyles.listboxOptionIcon}>
                  <CheckIcon className="w-5 h-5" aria-hidden="true" />
                </span>
              )}
            </li>
            {items.map((item) => (
              <li
                id={item.id.toString()}
                key={item.id}
                className={listboxStyles.listboxOption}
                onClick={() => {
                  onChange(item);
                  setIsOpen(false);
                }}
                role="option"
                aria-selected={value?.id === item.id}
              >
                <span
                  className={listboxStyles.listboxOptionText({
                    selected: value?.id === item.id,
                  })}
                >
                  {String(item[displayKey])}
                </span>
                {value?.id === item.id && (
                  <span className={listboxStyles.listboxOptionIcon}>
                    <CheckIcon className="w-5 h-5" aria-hidden="true" />
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <span className={listboxStyles.errorText}>{error}</span>}
    </div>
  );
};

export default ListboxComponent;

